// FurnitureInfoTabs.jsx — "Apžvalga / Prekės informacija / Svoris ir matmenys /
// Rinkinyje yra" skirtukai baldo details puslapyje, pagal IKEA produkto
// puslapio (pvz. LASTARE spintos derinys) informacijos architektūrą: po
// pagrindiniu (galerija + kaina + pirkimo) bloku eina platus skirtukų blokas
// su gilesne, susisteminta informacija — aprašymas, medžiagų/priežiūros ir
// dokumentų išskleidžiami sąrašai, tikslūs matmenys, ir ką konkrečiai gauna
// pirkėjas. Visi faktai čia yra REALŪS, paimti iš šio konkretaus baldo
// konfigūracijos (calculatePrice.js, pasirinktos plokštės) — niekas
// nesugalvota, kaip IKEA aprašymuose kartais būna bendro pobūdžio prekės
// tekstas.
//
// "Rinkinyje yra" rodo sekcijų (furnitureId grupių) korteles su mažu 3D
// peržiūros paveikslėliu — paspaudus, SectionDetailModal atidaro didesnį 3D
// vaizdą, techninį brėžinį ir tos KONKREČIOS sekcijos sudėtį (durelės,
// lentynos, furnitūra). Naudoja tą pačią useFurnitureDrawings fiksavimo
// logiką, kaip Suvestinė/FurnitureDrawingTabs — čia paleista PER SEKCIJĄ
// (aggregateByFurniture grupės), o ne per visą sujungtą baldą.
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    makeStyles, tokens, TabList, Tab, Spinner, Button,
    Accordion, AccordionHeader, AccordionItem, AccordionPanel,
} from '@fluentui/react-components';
import { iconMap } from '../../theme/icons';
import { Card, Badge } from '../../theme/primitives';
import { aggregateByFurniture } from '../../configurator/price/aggregateByFurniture';
import useFurnitureDrawings from '../../configurator/summary/useFurnitureDrawings';
import SectionDetailModal from './SectionDetailModal';
import {
    resolvePlateImage, getBasisImage, getBasisLabel, getEdgesImage, getEdgesLabel,
    getDoorTypeImage, getDoorTypeLabel,
} from '../../configurator/summary/materialThumbnails';

const TABS = [
    { labelKey: 'shop.furniture_info_tabs.tab_overview', icon: 'visibility' },
    { labelKey: 'shop.furniture_info_tabs.tab_product_info', icon: 'checklist' },
    { labelKey: 'shop.furniture_info_tabs.tab_weight_dims', icon: 'straighten' },
    { labelKey: 'shop.furniture_info_tabs.tab_kit_contents', icon: 'inventory_2' },
];
const QUALITY_KEYS = ['quality_0', 'quality_1', 'quality_2'];
const CheckIcon = iconMap.check;
const CAPTURE_SIZE = { width3d: 640, height3d: 460, width2d: 700, height2d: 440 };

const useStyles = makeStyles({
    rootCard: { overflow: 'hidden' },
    tabList: { overflowX: 'auto', borderBottom: `1px solid ${tokens.colorNeutralStroke2}` },
    panelWrap: {
        paddingLeft: tokens.spacingHorizontalL, paddingRight: tokens.spacingHorizontalL,
        paddingTop: tokens.spacingVerticalL, paddingBottom: tokens.spacingVerticalL,
        '@media (min-width: 1024px)': { padding: tokens.spacingHorizontalXXL },
    },
    accordionCard: {
        border: `1px solid ${tokens.colorNeutralStroke2}`, borderRadius: tokens.borderRadius2XLarge,
        overflow: 'hidden', backgroundColor: tokens.colorNeutralBackground1,
    },

    overviewGrid: { display: 'grid', gridTemplateColumns: '1fr', gap: tokens.spacingHorizontalXXL, '@media (min-width: 1024px)': { gridTemplateColumns: '1fr 1fr' } },
    badgeRow: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalM, marginBottom: tokens.spacingVerticalS },
    codeText: { fontFamily: tokens.fontFamilyMonospace, fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3 },
    descriptionText: { fontSize: tokens.fontSizeBase300, color: tokens.colorNeutralForeground2, lineHeight: '1.6' },
    moreInfoCol: { display: 'flex', flexDirection: 'column', gap: '10px' },
    eyebrow: { fontSize: tokens.fontSizeBase200, fontWeight: tokens.fontWeightSemibold, color: tokens.colorNeutralForeground3, textTransform: 'uppercase', letterSpacing: '0.05em' },

    specList: { display: 'flex', flexDirection: 'column', '> *:not(:last-child)': { borderBottom: `1px solid ${tokens.colorNeutralStroke2}` } },
    specRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: tokens.spacingHorizontalM, paddingTop: '10px', paddingBottom: '10px' },
    specLabel: { display: 'flex', alignItems: 'center', gap: '10px', fontSize: tokens.fontSizeBase300, color: tokens.colorNeutralForeground3, minWidth: 0 },
    specLabelText: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
    // Fiksuoto dydžio konteineris tiek nuotraukai, tiek piktogramai — kad
    // eilutės su realia nuotrauka ir eilutės tik su piktograma turėtų
    // VIENODĄ aukštį/lygiavimą (žr. /admin/bugs "Reikia sutvarkyti lygiavimus").
    specIconWrap: { width: '28px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
    specImage: { width: '100%', height: '100%', borderRadius: tokens.borderRadiusMedium, objectFit: 'cover', border: `1px solid ${tokens.colorNeutralStroke2}` },
    specIcon: { color: tokens.colorNeutralForeground2 },
    specValue: { fontSize: tokens.fontSizeBase300, fontWeight: tokens.fontWeightSemibold, color: tokens.colorNeutralForeground1, textAlign: 'right', flexShrink: 0, fontVariantNumeric: 'tabular-nums' },

    infoCol: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS },
    // Realus flex sąrašas su CheckIcon (ne naršyklės numatytieji <li> markeriai,
    // kurie nelygiuoja per teksto eilutės aukštį) — ta pati vizualinė kalba,
    // kaip CarpenterBanner.jsx/HowToChoosePanels.jsx patikros sąrašai.
    usefulList: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS, margin: 0, padding: 0, listStyle: 'none' },
    usefulItem: { display: 'flex', alignItems: 'flex-start', gap: '10px', color: tokens.colorNeutralForeground2, fontSize: tokens.fontSizeBase300, lineHeight: '1.5' },
    usefulIcon: { color: tokens.colorBrandForeground2, flexShrink: 0, fontSize: '16px', marginTop: '2px' },

    // Realus Fluent <Button appearance="transparent"> — tik per visą eilutę
    // ištemptas, kairėje lygiuotas išdėstymas (žr. #5).
    assemblyDocsRow: {
        width: '100%', justifyContent: 'flex-start',
        paddingTop: tokens.spacingVerticalXS, paddingBottom: tokens.spacingVerticalXS,
        color: tokens.colorNeutralForeground2,
        ':hover': { color: tokens.colorBrandForeground2 },
    },
    assemblyDocsLabel: { flex: 1, fontSize: tokens.fontSizeBase300, fontWeight: tokens.fontWeightMedium },

    materialsList: { display: 'flex', flexDirection: 'column', gap: tokens.spacingHorizontalM, marginBottom: tokens.spacingVerticalL },
    materialRow: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalM },
    materialTextWrap: { minWidth: 0 },
    materialImage: { width: '40px', height: '40px', borderRadius: tokens.borderRadiusLarge, objectFit: 'cover', border: `1px solid ${tokens.colorNeutralStroke2}`, flexShrink: 0 },
    materialName: { fontSize: tokens.fontSizeBase300, fontWeight: tokens.fontWeightSemibold, color: tokens.colorNeutralForeground1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', margin: 0 },
    materialMeta: { fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3, margin: 0 },
    careText: { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase300, lineHeight: '1.6' },

    safetyBox: {
        display: 'flex', alignItems: 'flex-start', gap: '10px', padding: tokens.spacingHorizontalM,
        borderRadius: tokens.borderRadiusMedium, backgroundColor: tokens.colorStatusWarningBackground1,
        border: `1px solid ${tokens.colorStatusWarningBorder1}`,
    },
    safetyIcon: { color: tokens.colorStatusWarningForeground1, flexShrink: 0 },
    safetyText: { color: tokens.colorStatusWarningForeground1, fontSize: tokens.fontSizeBase300, lineHeight: '1.5', margin: 0 },

    dimsWrap: { maxWidth: '28rem' },
    dimsEyebrow: { fontSize: tokens.fontSizeBase200, fontWeight: tokens.fontWeightSemibold, color: tokens.colorNeutralForeground3, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '10px' },
    dimsList: {
        borderRadius: tokens.borderRadiusXLarge, border: `1px solid ${tokens.colorNeutralStroke2}`, overflow: 'hidden',
        '> *:not(:last-child)': { borderBottom: `1px solid ${tokens.colorNeutralStroke2}` },
    },
    dimRow: {
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: tokens.spacingHorizontalM,
        paddingLeft: tokens.spacingHorizontalL, paddingRight: tokens.spacingHorizontalL,
        paddingTop: tokens.spacingVerticalM, paddingBottom: tokens.spacingVerticalM, backgroundColor: tokens.colorNeutralBackground1,
    },
    dimLabel: { display: 'flex', alignItems: 'center', gap: '10px', fontSize: tokens.fontSizeBase300, color: tokens.colorNeutralForeground3 },
    dimValue: { fontSize: tokens.fontSizeBase400, fontWeight: tokens.fontWeightBold, color: tokens.colorNeutralForeground1, fontVariantNumeric: 'tabular-nums' },

    modulesIntro: { fontSize: tokens.fontSizeBase300, color: tokens.colorNeutralForeground3, marginBottom: tokens.spacingVerticalM },
    modulesGrid: {
        display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: tokens.spacingHorizontalS,
        '@media (min-width: 640px)': { gridTemplateColumns: 'repeat(3, 1fr)' },
        '@media (min-width: 1024px)': { gridTemplateColumns: 'repeat(4, 1fr)' },
        '@media (min-width: 1280px)': { gridTemplateColumns: 'repeat(5, 1fr)' },
    },
    // Realus Fluent <Button appearance="outline"> — tik kairėje lygiuotas,
    // per visą aukštį ištemptas turinys (thumb + dviejų eilučių tekstas),
    // žr. #5.
    moduleBtn: {
        height: 'auto', width: '100%', justifyContent: 'flex-start', gap: '10px',
    },
    moduleThumb: { width: '44px', height: '44px', borderRadius: tokens.borderRadiusMedium, backgroundColor: tokens.colorNeutralBackground3, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, overflow: 'hidden' },
    moduleThumbImg: { width: '100%', height: '100%', objectFit: 'contain' },
    moduleTextWrap: { minWidth: 0 },
    moduleTitle: { fontSize: tokens.fontSizeBase200, fontWeight: tokens.fontWeightSemibold, color: tokens.colorNeutralForeground1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', margin: 0 },
    moduleSub: { fontSize: '11px', color: tokens.colorNeutralForeground3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', margin: 0 },
    modulesEmpty: { fontSize: tokens.fontSizeBase300, color: tokens.colorNeutralForeground3 },
});

// Fluent's real <Accordion> panašus per klik-atidaryk/uždaryk elgesį — plonas
// vietinis apvalkalas suteikia tą pačią rėmelio/fono "kortelės" išvaizdą,
// kurią anksčiau turėjo custom Accordion, o open/close būseną dabar valdo
// pats Fluent komponentas (defaultOpenItems), atskiro useState nebereikia.
const InfoDisclosure = ({ title, children, defaultOpen = false }) => {
    const s = useStyles();
    return (
        <Accordion collapsible defaultOpenItems={defaultOpen ? ['panel'] : []} className={s.accordionCard}>
            <AccordionItem value="panel">
                <AccordionHeader>{title}</AccordionHeader>
                <AccordionPanel>{children}</AccordionPanel>
            </AccordionItem>
        </Accordion>
    );
};

// Vaizdo/piktogramos + pavadinimo + reikšmės eilutė — ta pati vizualinė
// kalba, kaip anksčiau naudota dešiniojo stulpelio "Charakteristikos"
// kortelėje (getBasisImage/getEdgesImage realūs pasirinktų variantų
// paveikslėliai, ne bendrinės piktogramos, kur tik jie egzistuoja).
const SpecRow = ({ image, icon: Icon, label, value }) => {
    const s = useStyles();
    return (
        <div className={s.specRow}>
            <span className={s.specLabel}>
                <span className={s.specIconWrap}>
                    {image
                        ? <img src={image} alt="" className={s.specImage} />
                        : <Icon className={s.specIcon} style={{ fontSize: 18 }} />}
                </span>
                <span className={s.specLabelText}>{label}</span>
            </span>
            <span className={s.specValue}>{value}</span>
        </div>
    );
};

const AssemblyDocsRow = ({ onOpen }) => {
    const s = useStyles();
    const { t } = useTranslation();
    const DescriptionIcon = iconMap.description;
    const OpenIcon = iconMap.open_in_new;
    return (
        <Button
            appearance="transparent"
            onClick={onOpen}
            icon={<DescriptionIcon style={{ fontSize: 20 }} />}
            className={s.assemblyDocsRow}
        >
            <span className={s.assemblyDocsLabel}>{t('shop.furniture_info_tabs.assembly_docs')}</span>
            <OpenIcon style={{ fontSize: 18 }} />
        </Button>
    );
};

// Visos SKIRTINGOS plokščių spalvos/kodai, panaudoti bet kur šiame baldo
// vienete (korpusas + kiekviena durelių grupė) — ta pati logika, kaip
// SummaryView.jsx buildMaterialsLegend (PDF eksporte), pritaikyta čia.
const collectMaterials = (settings, doorPanels) => {
    const seen = new Map();
    // `plate.manufacturer` only exists on a real DB-linked Panel document —
    // several call sites elsewhere fall back to a minimal placeholder
    // ({panelType, price, code:'STD'}) when nothing was actually selected,
    // which has no manufacturer/name/color to show here.
    const add = (plate) => { if (plate?.code && plate?.manufacturer && !seen.has(plate.code)) seen.set(plate.code, plate); };
    add(settings.selectedPlate);
    if (settings.separatePlates) {
        add(settings.leftPlate); add(settings.topPlate); add(settings.bottomPlate); add(settings.rightPlate);
    }
    (doorPanels || []).forEach(d => add(d.plate));
    return Array.from(seen.values());
};

const MaterialsAndCare = ({ materials }) => {
    const s = useStyles();
    const { t } = useTranslation();
    return (
        <div>
            {materials.length === 0 ? (
                <p className={s.careText}>{t('shop.furniture_info_tabs.materials_care_empty')}</p>
            ) : (
                <div className={s.materialsList}>
                    {materials.map(m => (
                        <div key={m.code} className={s.materialRow}>
                            {resolvePlateImage(m) && (
                                <img src={resolvePlateImage(m)} alt="" className={s.materialImage} />
                            )}
                            <div className={s.materialTextWrap}>
                                <p className={s.materialName}>{m.manufacturer} {m.name}</p>
                                <p className={s.materialMeta}>{m.color}{m.color_tone ? ` · ${m.color_tone}` : ''} · {m.panelType === 1 ? 'MDF' : 'LMDP'}</p>
                            </div>
                        </div>
                    ))}
                </div>
            )}
            <p className={s.careText}>{t('shop.furniture_info_tabs.materials_care_text')}</p>
        </div>
    );
};

const SafetyNotice = () => {
    const s = useStyles();
    const { t } = useTranslation();
    const WarningIcon = iconMap.warning;
    return (
        <div className={s.safetyBox}>
            <WarningIcon className={s.safetyIcon} style={{ fontSize: 18 }} />
            <p className={s.safetyText} dangerouslySetInnerHTML={{ __html: t('shop.furniture_info_tabs.safety_text') }} />
        </div>
    );
};

const FurnitureInfoTabs = ({ furniture, typeLabel, onOpenAssemblyPdf }) => {
    const s = useStyles();
    const { t } = useTranslation();
    const [active, setActive] = useState(0);
    const settings = useMemo(() => ({
        ...(furniture.settings || {}),
        cabinets: furniture.cabinets || [],
        sectionOptions: furniture.settings?.sectionOptions || {},
    }), [furniture]);

    const { perFurniture: modules } = useMemo(
        () => (furniture.cabinets?.length ? aggregateByFurniture(furniture.cabinets, settings) : { perFurniture: [] }),
        [furniture.cabinets, settings]
    );
    // Sekcijos = konkretūs korpuso moduliai (kiekvienas — savo furnitureId grupė).
    // Visos sekcijos suminis durelių/furnitūros sąrašas — naudojamas "Prekės
    // informacija" faktų sąraše ir "Medžiagos ir priežiūra".
    const allDoorPanels = useMemo(() => modules.flatMap(m => m.calc?.doorPanels || []), [modules]);
    const materials = useMemo(() => collectMaterials(settings, allDoorPanels), [settings, allDoorPanels]);

    const doorTypeCounts = useMemo(() => {
        const counts = new Map();
        allDoorPanels.forEach((d) => {
            const groupEntry = (settings.mergedSections || []).find(g => g.groupId === d.groupId);
            const doorTypeValue = groupEntry
                ? settings.doorColors?.[`${groupEntry.furnitureId}-${groupEntry.cabinetIndex}-${groupEntry.sectionIndex}`]
                : null;
            const label = getDoorTypeLabel(doorTypeValue) || t('shop.furniture_info_tabs.door_fallback');
            const key = `${doorTypeValue ?? 'x'}-${label}`;
            const existing = counts.get(key);
            counts.set(key, { doorTypeValue, label, qty: (existing?.qty || 0) + 1 });
        });
        return Array.from(counts.values());
    }, [allDoorPanels, settings, t]);

    const shelfCount = (furniture.cabinets || []).reduce((sum, c) => sum + (c.shelves?.length || 0), 0);
    const w = settings.generalWidth, h = settings.generalHeight, d = settings.wallDepth;
    const sectionCount = furniture.cabinets?.length || 0;

    const qualityLabel = t(`shop.furniture_info_tabs.${QUALITY_KEYS[settings.quality] || QUALITY_KEYS[0]}`);
    const overviewDescription = [
        t('shop.furniture_info_tabs.overview_desc', { typeLabel, code: furniture.code }),
        sectionCount > 0 ? t(`shop.furniture_info_tabs.overview_sections_${sectionCount === 1 ? 'one' : 'other'}`, { count: sectionCount, quality: qualityLabel.toLowerCase() }) : null,
    ].filter(Boolean).join(' ');

    // "Rinkinyje yra" sekcijų kortelių 3D peržiūros paveikslėliai fiksuojami TIK
    // kai vartotojas realiai atidaro šį skirtuką (ne iš karto puslapiui
    // užsikrovus) — kiekvienas taikinys atidaro savo WebGL kontekstą (žr.
    // useFurnitureDrawings.jsx capturePhase/neededKeys pastabas). Iš pradžių
    // fiksuojama tik "Išorė" (kortelių miniatiūroms); atidaromo dialogo modulio
    // "Vidus" fiksuojamas papildomai, tik paprašius (žr. openModuleId žemiau).
    const [visitedRinkinys, setVisitedRinkinys] = useState(false);
    useEffect(() => { if (active === 3) setVisitedRinkinys(true); }, [active]);

    const [openModuleIndex, setOpenModuleIndex] = useState(null);
    const openModule = openModuleIndex != null ? (modules[openModuleIndex] || null) : null;

    const neededKeys = useMemo(() => {
        if (!visitedRinkinys) return [];
        const keys = modules.map(m => `${m.furnitureId}-exterior`);
        if (openModule) keys.push(`${openModule.furnitureId}-interior`);
        return keys;
    }, [visitedRinkinys, modules, openModule]);

    const { images, capturing, hiddenNode } = useFurnitureDrawings(visitedRinkinys, modules, settings, CAPTURE_SIZE, neededKeys);

    return (
        <Card padding={0} className={s.rootCard}>
            <TabList
                selectedValue={active}
                onTabSelect={(_e, data) => setActive(data.value)}
                className={s.tabList}
            >
                {TABS.map(({ labelKey, icon }, i) => {
                    const TabIcon = iconMap[icon];
                    return <Tab key={labelKey} value={i} icon={<TabIcon style={{ fontSize: 16 }} />}>{t(labelKey)}</Tab>;
                })}
            </TabList>

            <div className={s.panelWrap}>
                {/* ── Apžvalga ── */}
                {active === 0 && (
                    <div className={s.overviewGrid}>
                        <div>
                            <div className={s.badgeRow}>
                                <Badge color="gray" label={typeLabel} />
                                <span className={s.codeText}>{furniture.code}</span>
                            </div>
                            <p className={s.descriptionText}>{overviewDescription}</p>
                        </div>
                        <div className={s.moreInfoCol}>
                            <p className={s.eyebrow}>{t('shop.furniture_info_tabs.more_info')}</p>
                            <InfoDisclosure title={t('shop.furniture_info_tabs.materials_care')}>
                                <MaterialsAndCare materials={materials} />
                            </InfoDisclosure>
                        </div>
                    </div>
                )}

                {/* ── Prekės informacija ── */}
                {active === 1 && (
                    <div className={s.overviewGrid}>
                        <div className={s.specList}>
                            <SpecRow icon={iconMap.grid_view} label={t('shop.furniture_info_tabs.spec_sections')} value={`${sectionCount} ${t('shop.furniture_info_tabs.unit_pcs')}`} />
                            {shelfCount > 0 && <SpecRow icon={iconMap.shelves} label={t('shop.furniture_info_tabs.spec_shelves')} value={`${shelfCount} ${t('shop.furniture_info_tabs.unit_pcs')}`} />}
                            {doorTypeCounts.map(({ doorTypeValue, label, qty }) => (
                                <SpecRow key={`${doorTypeValue}-${label}`} image={getDoorTypeImage(doorTypeValue)} icon={iconMap.door_front} label={label} value={`${qty} ${t('shop.furniture_info_tabs.unit_pcs')}`} />
                            ))}
                            {settings.basisType != null && (
                                <SpecRow image={getBasisImage(settings.basisType)} icon={iconMap.foundation} label={t('shop.furniture_info_tabs.spec_basis')} value={getBasisLabel(settings.basisType)} />
                            )}
                            {settings.edges != null && settings.edges !== 0 && (
                                <SpecRow image={getEdgesImage(settings.edges)} icon={iconMap.border_all} label={t('shop.furniture_info_tabs.spec_edges')} value={getEdgesLabel(settings.edges)} />
                            )}
                            <SpecRow icon={iconMap.crop_square} label={t('shop.furniture_info_tabs.spec_back_wall')} value={settings.backWall ? t('shop.furniture_info_tabs.yes') : t('shop.furniture_info_tabs.no')} />
                        </div>
                        <div className={s.infoCol}>
                            <InfoDisclosure title={t('shop.furniture_info_tabs.useful_title')} defaultOpen>
                                <ul className={s.usefulList}>
                                    {[
                                        t('shop.furniture_info_tabs.useful_0'),
                                        t('shop.furniture_info_tabs.useful_1'),
                                        t('shop.furniture_info_tabs.useful_2'),
                                        t('shop.furniture_info_tabs.useful_3'),
                                    ].map(text => (
                                        <li key={text} className={s.usefulItem}>
                                            <CheckIcon className={s.usefulIcon} />
                                            <span>{text}</span>
                                        </li>
                                    ))}
                                </ul>
                            </InfoDisclosure>
                            <InfoDisclosure title={t('shop.furniture_info_tabs.safety_title')}>
                                <SafetyNotice />
                            </InfoDisclosure>
                        </div>
                    </div>
                )}

                {/* ── Svoris ir matmenys ── */}
                {active === 2 && (
                    <div className={s.dimsWrap}>
                        <p className={s.dimsEyebrow}>{t('shop.furniture_info_tabs.dims_title')}</p>
                        {/* Vieno stulpelio eilučių sąrašas, ne 2 stulpelių tinklelis — su
                            3 elementais tinklelis paliko nelygią, tuščią antrą eilutę. */}
                        <div className={s.dimsList}>
                            {[
                                w && { icon: iconMap.straighten, label: t('shop.furniture_info_tabs.dim_width'), value: `${(w * 100).toFixed(0)} cm` },
                                d && { icon: iconMap.open_in_full, label: t('shop.furniture_info_tabs.dim_depth'), value: `${d} cm` },
                                h && { icon: iconMap.height, label: t('shop.furniture_info_tabs.dim_height'), value: `${(h * 100).toFixed(0)} cm` },
                            ].filter(Boolean).map((dim) => {
                                const DimIcon = dim.icon;
                                return (
                                    <div key={dim.label} className={s.dimRow}>
                                        <span className={s.dimLabel}>
                                            <DimIcon style={{ fontSize: 18 }} />
                                            {dim.label}
                                        </span>
                                        <span className={s.dimValue}>{dim.value}</span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* ── Rinkinyje yra ── */}
                {active === 3 && (
                    <div>
                        <p className={s.modulesIntro}>{t('shop.furniture_info_tabs.modules_intro')}</p>
                        <div className={s.modulesGrid}>
                            {modules.map((m, i) => {
                                const thumb = images[`${m.furnitureId}-exterior`]?.img3d;
                                const sectionLabel = t('shop.furniture_info_tabs.section_label', { n: i + 1 });
                                return (
                                    <Button
                                        key={m.furnitureId}
                                        appearance="outline"
                                        onClick={() => setOpenModuleIndex(i)}
                                        className={s.moduleBtn}
                                    >
                                        <div className={s.moduleThumb}>
                                            {thumb ? (
                                                <img src={thumb} alt={sectionLabel} className={s.moduleThumbImg} />
                                            ) : (
                                                <Spinner size="tiny" />
                                            )}
                                        </div>
                                        <div className={s.moduleTextWrap}>
                                            <p className={s.moduleTitle}>{sectionLabel}</p>
                                            <p className={s.moduleSub}>
                                                {(m.width * 100).toFixed(0)}×{(m.height * 100).toFixed(0)}×{(m.depth * 100).toFixed(0)} cm
                                            </p>
                                        </div>
                                    </Button>
                                );
                            })}
                            {modules.length === 0 && (
                                <p className={s.modulesEmpty}>{t('shop.furniture_info_tabs.modules_empty')}</p>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {hiddenNode}
            {openModule && (
                <SectionDetailModal
                    module={openModule}
                    sectionLabel={t('shop.furniture_info_tabs.section_label', { n: openModuleIndex + 1 })}
                    code={furniture.code}
                    settings={settings}
                    images={images}
                    capturing={capturing}
                    onClose={() => setOpenModuleIndex(null)}
                />
            )}
        </Card>
    );
};

export default FurnitureInfoTabs;
